import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import { Briefcase, ArrowLeft, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { AppContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

function ChatPage({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { socket, setSocket } = useContext(AppContext);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    connectWebSocket();
    
    // Handle navigation state for new conversation
    if (location.state?.targetUserId) {
      createConversation(location.state.targetUserId);
    }

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on('message', (message) => {
        if (message.conversation_id === selectedConversation?.conversation_id) {
          setMessages((prev) => [...prev, message]);
        }
        fetchConversations(); // Refresh conversation list
      });
    }
  }, [socket, selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const connectWebSocket = () => {
    if (!socket) {
      const newSocket = io(WS_URL, {
        path: '/ws/' + user.user_id,
        transports: ['websocket'],
      });
      setSocket(newSocket);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`, { withCredentials: true });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const createConversation = async (targetUserId) => {
    try {
      const response = await axios.post(
        `${API}/conversations`,
        { participant_ids: [user.user_id, targetUserId] },
        { withCredentials: true }
      );
      await fetchConversations();
      const conv = await axios.get(`${API}/conversations`, { withCredentials: true });
      const newConv = conv.data.find((c) => c.conversation_id === response.data.conversation_id);
      if (newConv) {
        selectConversation(newConv);
      }
    } catch (error) {
      toast.error('Failed to create conversation');
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    try {
      const response = await axios.get(`${API}/conversations/${conv.conversation_id}/messages`, {
        withCredentials: true,
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      await axios.post(
        `${API}/conversations/${selectedConversation.conversation_id}/messages`,
        { content: newMessage },
        { withCredentials: true }
      );
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="chat-page">
      <div className="container mx-auto px-6 md:px-12 py-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="back-button">
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back
        </Button>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden" style={{ height: '80vh' }}>
          <div className="flex h-full">
            {/* Conversations Sidebar */}
            <div className="w-1/3 border-r border-slate-200 overflow-y-auto" data-testid="conversations-list">
              <div className="p-4 bg-primary text-white">
                <h2 className="text-xl font-outfit font-semibold">Messages</h2>
              </div>
              <div className="divide-y divide-slate-200">
                {conversations.map((conv) => (
                  <div
                    key={conv.conversation_id}
                    onClick={() => selectConversation(conv)}
                    className={`p-4 cursor-pointer hover:bg-slate-50 ${
                      selectedConversation?.conversation_id === conv.conversation_id ? 'bg-slate-100' : ''
                    }`}
                    data-testid="conversation-item"
                  >
                    <p className="font-medium text-primary">
                      {conv.participants_info.map((p) => p.name).join(', ')}
                    </p>
                    {conv.last_message && (
                      <p className="text-sm text-slate-600 truncate">{conv.last_message.content}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Chat Area */}
            {selectedConversation ? (
              <div className="flex-1 flex flex-col">
                <div className="p-4 bg-slate-100 border-b border-slate-200">
                  <h3 className="font-outfit font-semibold text-primary">
                    {selectedConversation.participants_info.map((p) => p.name).join(', ')}
                  </h3>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="messages-container">
                  {messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_id === user.user_id ? 'justify-end' : 'justify-start'}`}
                      data-testid="message-item"
                    >
                      <div
                        className={`max-w-[70%] rounded-lg px-4 py-2 ${
                          msg.sender_id === user.user_id
                            ? 'bg-primary text-white'
                            : 'bg-slate-200 text-slate-900'
                        }`}
                      >
                        {msg.sender_id !== user.user_id && (
                          <p className="text-xs font-medium mb-1">{msg.sender_name}</p>
                        )}
                        <p>{msg.content}</p>
                        {msg.is_flagged && (
                          <p className="text-xs text-red-400 mt-1">⚠️ Flagged by moderator</p>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <form onSubmit={sendMessage} className="p-4 border-t border-slate-200 flex gap-2" data-testid="message-form">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button type="submit" className="bg-accent-orange" data-testid="send-button">
                    <Send className="h-5 w-5" />
                  </Button>
                </form>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-500" data-testid="no-conversation">
                <p>Select a conversation to start chatting</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatPage;
